/**
 * Wave 4 — Submission cockpit: blockers with reasons + Approval Runtime.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  List,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ENTITY_TYPE_EPC_TENDER_SUBMISSION } from '@/constants/approval-entity-types';
import { epcCommercialService } from '@/services/epcCommercial.service';
import {
  buildSubmissionReadiness,
  readinessTagColor,
  seedSubmissionPack,
  type StageReadinessState,
} from './estimateProposalSubmissionReadiness';

const { Text, Title } = Typography;

export type EpcSubmissionCockpitProps = {
  tenderId: string;
  tender: Record<string, unknown>;
  submission: Record<string, unknown> | null;
  proposals: Record<string, unknown>[];
  bidDecisions: Record<string, unknown>[];
  openClarificationCount?: number;
  unincorporatedAddendaCount?: number;
  bidBondReady?: boolean;
  onCreated?: (row: Record<string, unknown>) => void;
  onUpdated?: () => void;
};

function parsePack(raw: unknown): Record<string, boolean> {
  const base = seedSubmissionPack();
  if (!raw) return base;
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return base;
    }
  }
  if (!obj || typeof obj !== 'object') return base;
  const root = obj as Record<string, unknown>;
  const checklist =
    root.checklist && typeof root.checklist === 'object'
      ? (root.checklist as Record<string, unknown>)
      : root;
  const next = { ...base };
  for (const k of Object.keys(next)) {
    if (checklist[k] === true) next[k] = true;
  }
  return next;
}

export const EpcSubmissionCockpit: React.FC<EpcSubmissionCockpitProps> = ({
  tenderId,
  tender,
  submission,
  proposals,
  bidDecisions,
  openClarificationCount = 0,
  unincorporatedAddendaCount = 0,
  bidBondReady,
  onCreated,
  onUpdated,
}) => {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const id = submission ? String(submission.id || '') : '';

  const hasApprovedGo = bidDecisions.some((b) => {
    const outcome = String(b.outcome || '').toUpperCase();
    const status = String(b.status || '').toLowerCase();
    return status === 'approved' && (outcome === 'GO' || outcome === 'CONDITIONAL');
  });

  const linkedProposal = useMemo(() => {
    if (!submission) return proposals[0] || null;
    const pid = String(submission.proposal_id || submission.proposalId || '');
    return proposals.find((p) => String(p.id) === pid) || proposals[0] || null;
  }, [submission, proposals]);

  const readiness = useMemo(() => {
    if (!submission) return null;
    return buildSubmissionReadiness({
      submission,
      proposal: linkedProposal,
      hasApprovedGo,
      submissionDueAt: String(tender.submission_due_at || tender.submissionDueAt || '') || null,
      openClarificationCount,
      unincorporatedAddendaCount,
      bidBondReady,
    });
  }, [submission, linkedProposal, hasApprovedGo, tender, openClarificationCount, unincorporatedAddendaCount, bidBondReady]);

  const pack = parsePack(submission?.pack_json ?? submission?.packJson);

  const createM = useMutation({
    mutationFn: (departmentId: string) =>
      epcCommercialService.createSubmission({
        tenderId,
        proposalId: proposals[0] ? String(proposals[0].id) : undefined,
        packJson: { checklist: seedSubmissionPack() },
        departmentId,
      }),
    onSuccess: (row) => {
      message.success('Submission draft created');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onCreated?.(row);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const patchM = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      epcCommercialService.patchSubmission(id, payload),
    onSuccess: () => {
      message.success('Submission updated');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const submitM = useMutation({
    mutationFn: () => epcCommercialService.submitSubmission(id),
    onSuccess: () => {
      message.success('Submitted for approval');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      qc.invalidateQueries({
        queryKey: ['approval-summary', ENTITY_TYPE_EPC_TENDER_SUBMISSION, id],
      });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Submit failed'),
  });

  if (!submission) {
    return (
      <Card size="small" title="Submission cockpit">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No submission draft yet"
          description="Create a pack checklist linked to the latest proposal."
        />
        <Button
          type="primary"
          disabled={!proposals.length}
          title={!proposals.length ? 'Create a proposal first' : undefined}
          onClick={() => setCreateOpen(true)}
        >
          Create submission
        </Button>
        {!proposals.length ? (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Create a proposal before starting the submission pack.
          </Text>
        ) : null}
        <Modal
          title="Create submission"
          open={createOpen}
          onCancel={() => {
            setCreateOpen(false);
            createForm.resetFields();
          }}
          onOk={() => createForm.submit()}
          confirmLoading={createM.isPending}
          destroyOnClose
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={(v) => createM.mutate(String(v.department_id))}
          >
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
            >
              <DepartmentSelect />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    );
  }

  const editable = !['Submitted', 'Approved'].includes(String(submission.status || ''));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space wrap>
        <Title level={5} style={{ margin: 0 }}>
          {String(submission.submission_number || 'Submission')}
        </Title>
        {readiness ? (
          <Tag color={readinessTagColor(readiness.overall as StageReadinessState)}>
            {readiness.overall}
          </Tag>
        ) : null}
        <Tag>{String(submission.status || 'Draft')}</Tag>
      </Space>

      <Card size="small" title="Linked proposal">
        <Select
          style={{ width: '100%' }}
          disabled={!editable || patchM.isPending}
          value={String(submission.proposal_id || submission.proposalId || undefined) || undefined}
          placeholder="Select proposal"
          options={proposals.map((p) => ({
            value: String(p.id),
            label: `${p.proposal_number || String(p.id).slice(0, 8)} — ${p.title || 'Proposal'}`,
          }))}
          onChange={(proposalId) => patchM.mutate({ proposalId })}
        />
      </Card>

      <Card size="small" title="Pack checklist">
        <List
          size="small"
          dataSource={Object.entries(pack)}
          renderItem={([key, checked]) => (
            <List.Item>
              <Checkbox
                checked={checked}
                disabled={!editable || patchM.isPending}
                onChange={(e) => {
                  const next = { ...pack, [key]: e.target.checked };
                  patchM.mutate({ packJson: { checklist: next } });
                }}
              >
                {key.replace(/_/g, ' ')}
              </Checkbox>
            </List.Item>
          )}
        />
      </Card>

      {readiness ? (
        <Card size="small" title="Blockers (with reasons)">
          {!readiness.blockers.length ? (
            <Alert type="success" showIcon message="No blockers — ready to submit" />
          ) : (
            <List
              size="small"
              dataSource={readiness.blockers}
              renderItem={(b) => (
                <List.Item>
                  <Tag color={b.severity === 'blocking' ? 'error' : 'warning'}>
                    {b.severity}
                  </Tag>
                  <Text>{b.reason}</Text>
                </List.Item>
              )}
            />
          )}
          <Button
            type="primary"
            style={{ marginTop: 12 }}
            disabled={!readiness.canSubmit}
            loading={submitM.isPending}
            title={readiness.submitBlockedReason}
            onClick={() => submitM.mutate()}
          >
            Submit for approval
          </Button>
          {!readiness.canSubmit && readiness.submitBlockedReason ? (
            <Alert
              style={{ marginTop: 8 }}
              type="info"
              showIcon
              message={readiness.submitBlockedReason}
            />
          ) : null}
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Submit starts Approval Runtime for `epc_tender_submission`. FE never sets Approved alone.
          </Text>
        </Card>
      ) : null}

      <Card size="small" title="Approval">
        {id ? (
          <ApprovalPanel
            entityType={ENTITY_TYPE_EPC_TENDER_SUBMISSION}
            entityId={id}
            extraInvalidateQueryKeys={[
              ['epc-tender-workspace', tenderId],
              ['epc-submission', id],
            ]}
          />
        ) : null}
      </Card>
    </Space>
  );
};

export default EpcSubmissionCockpit;
